var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/StoreAdapter.tsx
import {
  useConstant,
  useConstantCallback,
  useMemoCondition,
  useSubscription
} from "@lyonph/react-hooks";
import React, {
  useDebugValue,
  useEffect,
  useRef,
  useState
} from "react";
import {
  createNullaryModel,
  useScopedModelExists,
  useValue
} from "react-scoped-model";
var Notifier = class {
  constructor(value) {
    this.alive = true;
    this.listeners = new Set();
    this.value = value;
  }
  subscribe(listener) {
    if (this.alive) {
      this.listeners.add(listener);
    }
    return () => {
      if (this.alive) {
        this.listeners.delete(listener);
      }
    };
  }
  notify(value) {
    if (this.alive) {
      this.value = value;
      this.listeners.forEach((listener) => {
        listener(value);
      });
    }
  }
  destroy() {
    if (this.alive) {
      this.listeners.clear();
      this.alive = false;
    }
  }
  read() {
    return this.value;
  }
  hasListeners() {
    return this.listeners.size > 0;
  }
};
var index = 0;
function getIndex() {
  const current = index;
  index += 1;
  return current;
}
function defaultUpdate(prev, next) {
  return !Object.is(prev, next);
}
function defaultCleanup() {
}
function createStoreAdapter(options) {
  return __spreadValues({
    id: `Store-${getIndex()}`,
    shouldUpdate: defaultUpdate,
    keepAlive: false,
    onCleanup: defaultCleanup
  }, options);
}
var StoreAdapterCore = createNullaryModel(() => {
  const isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);
  const memory = useConstant(() => new Map());
  const registered = useConstant(() => new Set());
  const pendingStores = useConstant(() => new Set());
  const [pendingStoresVersion, setPendingStoresVersion] = useState([]);
  const pendingUpdates = useRef([]);
  const [pendingUpdatesVersion, setPendingUpdatesVersion] = useState([]);
  const batchUpdates = useConstantCallback((callback) => {
    setTimeout(() => {
      if (isMounted.current) {
        pendingUpdates.current.push(callback);
        setPendingUpdatesVersion([]);
      }
    });
  });
  useEffect(() => {
    if (pendingStores.size) {
      const stores = new Set(pendingStores);
      pendingStores.clear();
      stores.forEach((store) => {
        const instance = memory.get(store.id);
        if (instance && !registered.has(store)) {
          const checkForUpdates = () => {
            if (isMounted.current) {
              const nextValue = store.read();
              if (store.shouldUpdate(instance.notifier.read(), nextValue)) {
                batchUpdates(() => {
                  instance.notifier.notify(nextValue);
                });
              }
            }
          };
          const unsubscribe = store.subscribe(checkForUpdates);
          checkForUpdates();
          if (unsubscribe) {
            instance.unsubscribe = unsubscribe;
          }
          registered.add(store);
        }
      });
    }
  }, [batchUpdates, memory, pendingStores, pendingStoresVersion, registered]);
  useEffect(() => {
    const batch = pendingUpdates.current;
    if (batch.length) {
      pendingUpdates.current = [];
      batch.forEach((update) => {
        update();
      });
    }
  }, [pendingUpdatesVersion]);
  useEffect(() => () => {
    memory.forEach((item) => {
      if (item.unsubscribe) {
        item.unsubscribe();
      }
      item.notifier.destroy();
      item.reference.onCleanup();
    });
    memory.clear();
  }, [memory]);
  return useConstant(() => {
    const getInstance = (store) => {
      let instance = memory.get(store.id);
      if (!instance) {
        const proxy = new Notifier(store.read());
        instance = {
          reference: store,
          notifier: proxy
        };
        if (isMounted.current) {
          memory.set(store.id, instance);
        }
      }
      return instance;
    };
    let currentTask = [];
    let currentMarkTask = [];
    const markedStores = new Set();
    return {
      useRegister: (store) => {
        if (!registered.has(store)) {
          pendingStores.add(store);
          if (typeof window !== "undefined") {
            const newTask = [];
            currentTask = newTask;
            setTimeout(() => {
              if (currentTask === newTask && isMounted.current) {
                setPendingStoresVersion([]);
              }
            });
          }
        }
        useEffect(() => {
          if (isMounted.current) {
            setPendingStoresVersion([]);
          }
        }, []);
        useEffect(() => {
          if (isMounted.current && !markedStores.has(store)) {
            const newTask = [];
            currentMarkTask = newTask;
            markedStores.add(store);
            setTimeout(() => {
              if (currentMarkTask === newTask && isMounted.current) {
                memory.forEach((instance) => {
                  if (!markedStores.has(instance.reference)) {
                    if (instance.unsubscribe) {
                      instance.unsubscribe();
                    }
                    instance.notifier.destroy();
                    instance.reference.onCleanup();
                    memory.delete(instance.reference.id);
                    registered.delete(instance.reference);
                  }
                });
              }
            });
          }
        }, [store]);
      },
      read: (store) => getInstance(store).notifier.read(),
      subscribe: (store, callback) => {
        const instance = getInstance(store);
        const unsubscribe = instance.notifier.subscribe(callback);
        return () => {
          unsubscribe();
          if (!(store.keepAlive || instance.notifier.hasListeners())) {
            if (instance.unsubscribe) {
              instance.unsubscribe();
            }
            instance.notifier.destroy();
            instance.reference.onCleanup();
            memory.delete(store.id);
            registered.delete(store);
          }
        };
      }
    };
  });
}, {
  displayName: "StoreAdapterCore"
});
function useStoreAdapterRestriction() {
  const exists = useScopedModelExists(StoreAdapterCore);
  if (!exists) {
    throw new Error("Attempt to access missing StoreAdapterContext.");
  }
}
function identity(value) {
  return value;
}
function useStoreAdapter(store, options) {
  var _a, _b;
  useStoreAdapterRestriction();
  const getSnapshot = (_a = options == null ? void 0 : options.getSnapshot) != null ? _a : identity;
  const shouldUpdate = (_b = options == null ? void 0 : options.shouldUpdate) != null ? _b : defaultUpdate;
  const context = useValue(StoreAdapterCore);
  context.useRegister(store);
  const subscription = useMemoCondition(() => ({
    read: () => getSnapshot(context.read(store)),
    subscribe: (callback) => context.subscribe(store, callback),
    shouldUpdate
  }), {
    context,
    store,
    getSnapshot,
    shouldUpdate
  }, (prev, next) => !(Object.is(prev.store, next.store) && Object.is(prev.context, next.context) && Object.is(prev.getSnapshot, next.getSnapshot) && Object.is(prev.shouldUpdate, next.shouldUpdate)));
  const state = useSubscription(subscription);
  useDebugValue(state);
  return state;
}
var StoreAdapterRoot = ({ children }) => {
  const context = useScopedModelExists(StoreAdapterCore);
  if (context) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  }
  return /* @__PURE__ */ React.createElement(StoreAdapterCore.Provider, null, children);
};
if (process.env.NODE_ENV !== "production") {
  StoreAdapterRoot.displayName = "StoreAdapterRoot";
}
export {
  StoreAdapterRoot,
  createStoreAdapter,
  useStoreAdapter
};
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2021 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2021
 */
//# sourceMappingURL=index.js.map
